<template>
  <div class="cart-item-delivery-estimate" v-if="estimatedDeliveryMetafield">
    <div
      v-if="shouldShowMainBanner"
      class="cart-item-delivery-estimate__banner"
      @click="handleEddClick"
    >
      <img
        v-if="deliveryEstimateIcon"
        :src="deliveryEstimateIcon"
        alt="Icon"
      >
      <!-- eslint-disable-next-line vue/no-v-html -->
      <span
        v-if="formattedEddCopy"
        class="cart-item-delivery-estimate__status"
        v-html="formattedEddCopy"
      />
      <span
        v-else
        class="cart-item-delivery-estimate__status"
      >
        Estimated delivery date
      </span>
    </div>

    <div
      v-if="shouldShowSelectMethodMessage"
      class="cart-item-delivery-estimate__banner cart-item-delivery-estimate__banner--info"
    >
      <img
        v-if="deliveryEstimateIcon"
        :src="deliveryEstimateIcon"
        alt="Icon"
      >
      <!-- eslint-disable-next-line vue/no-v-html -->
      <span
        v-if="selectMethodMessage"
        class="cart-item-delivery-estimate__message"
        v-html="selectMethodMessage"
      />
      <span
        v-else
        class="cart-item-delivery-estimate__message"
      >
        Select <strong>"Drop off at a store"</strong> or
        <strong>"Ship it to us"</strong> to receive your estimated delivery date
      </span>
    </div>

    <div
      v-if="shouldShowPlaceholderBanner"
      class="cart-item-delivery-estimate__banner cart-item-delivery-estimate__banner--placeholder"
    >
      <img
        v-if="deliveryEstimateIcon"
        :src="deliveryEstimateIcon"
        alt="Icon"
      >
      <span class="cart-item-delivery-estimate__label">Estimated Delivery:</span>
      <span class="cart-item-delivery-estimate__status">TBD</span>
      <button
        type="button"
        class="cart-item-delivery-estimate__edit-link"
        @click="toggleZipcodeModal"
      >
        Enter your ZIP Code to estimate delivery
      </button>
    </div>

    <!-- User clicks on zip code to change or set -->
    <div
      v-if="showZipcodeModal"
      class="cart-item-delivery-estimate__modal"
      :aria-hidden="!showZipcodeModal"
    >
      <div class="cart-item-delivery-estimate__modal-content">
        <div class="cart-item-delivery-estimate__modal-header">
          <div class="cart-item-delivery-estimate__modal-header-icon">
            <i class="icon icon--shipping-truck" />
          </div>
          <h2 class="cart-item-delivery-estimate__modal-title">
            Shipping
          </h2>
          <button
            type="button"
            class="cart-item-delivery-estimate__modal-close"
            aria-label="Close zip code editor"
            @click="toggleZipcodeModal"
          >
            ×
          </button>
        </div>

        <div class="cart-item-delivery-estimate__modal-body">
          <label
            id="itemZipcodeLabel"
            for="itemZipcode"
            class="cart-item-delivery-estimate__modal-label"
          >
            Enter ZIP Code
          </label>
          <div class="cart-item-delivery-estimate__modal-input">
            <input
              id="itemZipcode"
              ref="zipcodeInput"
              v-model="newZipcode"
              type="text"
              placeholder="Enter ZIP Code"
              aria-labelledby="itemZipcodeLabel"
              pattern="\d{5,5}(-\d{4,4})?"
              @input="clearError"
              @keyup.enter="submitZipcode"
            >
            <button
              type="button"
              class="cart-item-delivery-estimate__modal-submit"
              aria-label="Submit ZIP code"
              :disabled="isLoading || isError"
              @click="submitZipcode"
            >
              <i class="icon icon--arrow-right--white" aria-hidden="true" />
            </button>
          </div>
          <div
            v-if="isError"
            class="cart-item-delivery-estimate__modal-error"
          >
            {{ errorMessage }}
          </div>

          <div class="cart-item-delivery-estimate__modal-info">
            <p>
              This estimated delivery date is based on where the frame is
              being shipped and how long we'll take to build and get it there.
            </p>
            <p>
              We now offer art drop-off and order pickup in our retail
              locations. You can select these options during checkout.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/order-in-components */
import { nextTick } from "vue";
import useDelivery from "../composables/delivery-estimator";
import GlazeClient from "@framebridge/toolbox/apihelpers/glazeClient";

export default {
  name: "CartItemDeliveryEstimate",
  props: {
    item: {
      type: Object,
      required: true
    },
    isPhysical: {
      type: Boolean,
      default: false
    },
    shippingMethodSelected: {
      type: Boolean,
      default: false
    },
    dropOffStoreProperty: {
      type: [String, Object],
      default: null
    },
    shipToUsProperty: {
      type: [String, Object],
      default: null
    },
    estimatedDeliveryMetafield: {
      type: [Boolean, String],
      default: null
    },
    bundleDetails: {
      type: Object,
      default: () => ({})
    },
    allItems: {
      type: Array,
      default: () => []
    },
    sharedZipcode: {
      type: String,
      default: null
    }
  },
  emits: ["zipcode-updated"],
  data() {
    return {
      delivery: null,
      zipcode: null,
      showZipcodeModal: false,
      newZipcode: "",
      isLoading: false,
      isError: false,
      errorMessage: "",
      itemDeliveryDate: null,
      overnightDeliveryDate: null,
      shipArtByDate: null,
      deliveryEstimateMessaging: null,
      initialEstimateFetched: false
    };
  },
  created() {
    this.delivery = useDelivery();
  },
  computed: {
    currentReceiveBy() {
      return this.delivery?.currentReceiveBy?.value || null;
    },
    // Check if item has estimated delivery date enabled
    hasEstimatedDeliveryDate() {
      if (!this.item) {
        return false;
      }

      // For bundle child items, check the metafield on the parent bundle item
      let metafieldToCheck = this.estimatedDeliveryMetafield;
      if (this.item.properties?._parent_bundle_spec) {
        const parentBundleSpec = this.item.properties._parent_bundle_spec;
        const parentBundleItem = this.allItems.find(
          (item) => item.properties?._bundle_spec_number === parentBundleSpec
        );
        metafieldToCheck =
          parentBundleItem?.metafield_estimated_delivery_date ||
          this.estimatedDeliveryMetafield;
      }

      // Check metafield first (primary control mechanism)
      if (metafieldToCheck === true || metafieldToCheck === "true") {
        return true;
      }

      // Fall back to product type and physical option checks
      const productType = this.item.product_type?.toLowerCase() || "";
      const productTypesWithEstimates = [
        "art print frame",
        "custom frame",
        "curated design frame",
        "frame bundle"
      ];

      const hasPhysicalOption = this.item.options_with_values?.some((opt) =>
        opt.value.toLowerCase().includes("physical")
      );

      const isConfiguredType = productTypesWithEstimates.some((type) =>
        productType.includes(type)
      );

      return hasPhysicalOption || isConfiguredType || this.isPhysical;
    },
    isPhysicalComputed() {
      return this.item.options_with_values?.some((opt) =>
        opt.value.toLowerCase().includes("physical")
      );
    },
    isShipToUs() {
      return Boolean(this.shipToUsProperty);
    },
    deliveryEstimateMessagingForCurrentDate() {
      if (!this.deliveryEstimateMessaging) {
        return null;
      }
      return this.deliveryEstimateMessaging.find((messaging) => {
        const inDateRange = this.isCurrentDateWithinRange(
          messaging.startDateTime,
          messaging.endDateTime
        );
        const digitalOrPhysical = this.isPhysical ? "physical" : "digital";
        const hasCartContent = messaging.cartContent !== null;
        return (
          inDateRange &&
          messaging.conveyance?.toLowerCase() === digitalOrPhysical &&
          hasCartContent
        );
      });
    },
    deliveryEstimateMessagingForCart() {
      const deliveryEstimatesObject =
        this.deliveryEstimateMessagingForCurrentDate?.cartContent.find(
          (content) => Object.keys(content).includes("deliveryEstimates")
        );
      return deliveryEstimatesObject?.deliveryEstimates;
    },
    deliveryEstimateIcon() {
      if (!this.deliveryEstimateMessagingForCart) {
        return null;
      }
      return this.deliveryEstimateMessagingForCart?.iconAsset;
    },
    deliveryEstimateSlaType() {
      if (!this.deliveryEstimateMessagingForCurrentDate) {
        return null;
      }
      return this.deliveryEstimateMessagingForCurrentDate?.slaType;
    },
    // Banner visibility
    shouldShowBanner() {
      if (this.item.properties?._parent_bundle_spec) {
        const parentBundleSpec = this.item.properties._parent_bundle_spec;
        const firstFrameInBundle = this.allItems.find(
          (item) => item.properties?._parent_bundle_spec === parentBundleSpec
        );
        return this.item.id === firstFrameInBundle?.id;
      }

      if (
        this.item.product_type === "Frame Bundle" &&
        this.item.properties?._bundle_spec_number
      ) {
        return true;
      }

      return true;
    },
    shouldShowMainBanner() {
      return (
        this.shouldShowBanner &&
        this.zipcode &&
        this.hasEstimatedDeliveryDate &&
        this.itemDeliveryDate &&
        (!this.isPhysicalComputed || this.shippingMethodSelected) &&
        !this.showZipcodeModal &&
        this.deliveryEstimateMessagingForCart
      );
    },
    shouldShowSelectMethodMessage() {
      return (
        this.shouldShowBanner &&
        !this.shippingMethodSelected &&
        this.isPhysicalComputed &&
        this.hasEstimatedDeliveryDate &&
        !this.showZipcodeModal
      );
    },
    shouldShowPlaceholderBanner() {
      if (this.shouldShowBanner === false || this.zipcode || this.showZipcodeModal) {
        return false;
      }

      if (!this.isPhysicalComputed) {
        return true;
      }

      return this.shippingMethodSelected && this.hasEstimatedDeliveryDate;
    },
    // Copy template selection
    eddCopyTemplate() {
      const messaging = this.deliveryEstimateMessagingForCart;
      if (!messaging) {
        return null;
      }

      if (!this.isPhysical) {
        return messaging.eddCopy || null;
      }

      if (this.isShipToUs) {
        return messaging.eddCopyMailIn || null;
      } else if (this.dropOffStoreProperty) {
        return messaging.eddCopyDropOff || null;
      }

      return messaging.eddCopyPhysicalDefault || null;
    },
    formattedEddCopy() {
      const template = this.eddCopyTemplate;
      if (!template) {
        return null;
      }

      let result = template;

      if (this.itemDeliveryDate) {
        result = result.replace("{date}", this.formatDate(this.itemDeliveryDate));
      }

      if (this.zipcode) {
        result = result.replace(
          "<b>{zip}</b>",
          "<button type=\"button\" class=\"cart-item-delivery-estimate__zipcode-button\" data-zipcode-button><b>" +
            this.zipcode +
            "</b></button>"
        );
        result = result.replace("{zip}", this.zipcode);
      }

      if (this.overnightDeliveryDate) {
        result = result.replace(
          "{dateExpeditedOneDay}",
          this.formatDate(this.overnightDeliveryDate)
        );
      }

      if (this.shipArtByDate) {
        result = result.replace("{dateDropOff}", this.formatDate(this.shipArtByDate));
        result = result.replace("{dateMailIn}", this.formatDate(this.shipArtByDate));
      }

      return result;
    },
    selectMethodMessage() {
      const messaging = this.deliveryEstimateMessagingForCart;
      if (!messaging) {
        return null;
      }
      return messaging.eddCopyPhysicalDefault || null;
    }
  },
  methods: {
    isCurrentDateWithinRange(startDate, endDate) {
      const now = new Date();
      const start = new Date(startDate);
      const end = new Date(endDate);
      return now >= start && now <= end;
    },
    formatDate(dateString) {
      if (!dateString) {
        return "";
      }
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC"
      });
    },
    toggleZipcodeModal() {
      this.showZipcodeModal = !this.showZipcodeModal;
      if (this.showZipcodeModal) {
        this.newZipcode = this.zipcode || "";
        nextTick(() => {
          this.$refs.zipcodeInput?.focus();
        });
      }
    },
    handleEddClick(event) {
      if (event.target.closest("[data-zipcode-button]")) {
        this.toggleZipcodeModal();
      }
    },
    clearError() {
      this.isError = false;
      this.errorMessage = "";
    },
    getFrameSpecForEstimate() {
      if (
        this.item.product_type === "Frame Bundle" &&
        this.item.properties?._bundle_spec_number
      ) {
        const bundleNumber = this.item.properties._bundle_spec_number;
        const bundleFrameSpecs = this.bundleDetails[bundleNumber];
        return bundleFrameSpecs?.[0];
      }

      if (this.item.properties?._parent_bundle_spec) {
        const parentBundleSpec = this.item.properties._parent_bundle_spec;
        const firstFrameInBundle = this.allItems.find(
          (item) => item.properties?._parent_bundle_spec === parentBundleSpec
        );
        return firstFrameInBundle?.properties?._frame_spec_number;
      }

      return this.item.properties?._frame_spec_number;
    },
    async fetchDeliveryEstimateMessaging() {
      // eslint-disable-next-line no-undef
      const baseURL = SDG.Data.joineryHost || "https://staging.framebridge.com";

      try {
        const messagingResponse = await new GlazeClient(null, { baseUrl: baseURL }).get(
          "api/v1/estimated_delivery_messaging"
        );
        this.deliveryEstimateMessaging =
          messagingResponse?.data?.estimatedDeliveryMessaging;
      } catch (error) {
        console.error("Error fetching delivery estimate messaging:", error);
      }
    },
    async fetchDeliveryEstimates(zipValue) {
      if (!zipValue) {
        return null;
      }

      if (!this.deliveryEstimateMessaging) {
        await this.fetchDeliveryEstimateMessaging();
        await nextTick();
      }

      const frameSpecNumber = this.getFrameSpecForEstimate();

      let customerPackaging = null;
      let dropOffInStore = null;
      let dropOffInStoreName = null;

      if (this.isPhysical) {
        const hasValidShipToUs =
          this.shipToUsProperty && this.shipToUsProperty !== "temp_ship";
        const hasDropOff = Boolean(this.dropOffStoreProperty);

        if (hasValidShipToUs) {
          customerPackaging = true;
          dropOffInStore = false;
          dropOffInStoreName = null;
        } else if (hasDropOff) {
          customerPackaging = false;
          dropOffInStore = true;
          dropOffInStoreName = this.dropOffStoreProperty;
        }
      }

      const estimateResponse = await this.delivery.fetchDeliveryEstimateOptions({
        zipcode: zipValue,
        frameSpecNumber,
        customerPackaging,
        dropOffInStore,
        dropOffInStoreName
      });

      if (!estimateResponse) {
        return null;
      }

      const estimates = this.isPhysical
        ? estimateResponse.physical
        : estimateResponse.digital;
      if (!estimates) {
        return null;
      }

      const slaType = this.deliveryEstimateSlaType?.toLowerCase();

      if (slaType && estimates[slaType]?.estimatedDeliveryDate) {
        this.itemDeliveryDate = estimates[slaType].estimatedDeliveryDate;
        this.shipArtByDate = estimates[slaType].shipArtByDate || null;
      }

      if (!this.isPhysical && estimates.overnight?.estimatedDeliveryDate) {
        this.overnightDeliveryDate = estimates.overnight.estimatedDeliveryDate;
      }

      return {
        deliveryDate: this.itemDeliveryDate,
        shipArtByDate: this.shipArtByDate,
        overnightDate: this.overnightDeliveryDate
      };
    },
    async submitZipcode() {
      const zip = this.newZipcode?.trim();

      if (!zip) {
        this.isError = true;
        this.errorMessage = "Please enter a zip code";
        return;
      }

      if (!this.$refs.zipcodeInput.checkValidity()) {
        this.isError = true;
        this.errorMessage = "Please enter a valid zip code (e.g., 12345 or 12345-6789)";
        return;
      }

      this.isLoading = true;
      try {
        const result = await this.fetchDeliveryEstimates(zip);

        if (result?.deliveryDate) {
          // Update local state and sessionStorage with new zip
          this.zipcode = zip;
          sessionStorage.setItem("SDG-zip", zip);
          this.delivery.updateZipcode(zip);
          this.showZipcodeModal = false;
          this.$emit("zipcode-updated", { zipcode: zip, deliveryDate: result.deliveryDate });
        } else {
          throw new Error("Could not retrieve delivery estimate");
        }
      } catch (error) {
        console.error("Error updating zipcode:", error);
        this.isError = true;
        this.errorMessage = "Unable to update zip code. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },
    async fetchEstimate(zipValue) {
      if (!zipValue) {
        return;
      }
      await this.fetchDeliveryEstimates(zipValue);
    },
    handleShippingMethodChange(newDropOff, newShipToUs) {
      if (!this.zipcode) {
        return;
      }

      // "temp_ship" means they picked ship to us, but didn't select packaging yet
      const hasValidShipToUs = newShipToUs && newShipToUs !== "temp_ship";
      const hasValidDropOff = Boolean(newDropOff);

      if (hasValidShipToUs || hasValidDropOff) {
        this.fetchDeliveryEstimates(this.zipcode);
      }
    }
  },
  watch: {
    // If zipcode is updated from any component, refetch estimate
    sharedZipcode: {
      immediate: true,
      handler(newZip) {
        if (newZip && newZip !== this.zipcode) {
          this.zipcode = newZip;

          const isDigital = !this.isPhysical;
          const isBundle = this.item.product_type === "Frame Bundle";

          if (isDigital || isBundle || this.shippingMethodSelected) {
            this.fetchEstimate(newZip);
          }
        }
      }
    },
    // If there's a change to shipping methods (drop off in store or ship to us), refetch estimate
    dropOffStoreProperty(newDropOff) {
      this.handleShippingMethodChange(newDropOff, this.shipToUsProperty);
    },
    shipToUsProperty(newShipToUs) {
      this.handleShippingMethodChange(this.dropOffStoreProperty, newShipToUs);
    }
  }
};
</script>

<style scoped>
.cart-item-delivery-estimate {
  margin-bottom: 1rem;
  width: 100%;
}

.cart-item-delivery-estimate__banner {
  align-items: center;
  background-color: #f0f0f0;
  border: 1px solid #1d2019;
  border-radius: 0;
  box-sizing: border-box;
  display: flex;
  font-size: 0.875rem;
  font-weight: 400;
  gap: 0.50rem;
  line-height: 1.5;
  padding: 1rem;
  width: 100%;
}

.cart-item-delivery-estimate__banner--placeholder {
  background-color: #f0f0f0;
  border: 1px solid #1d2019;
}

.cart-item-delivery-estimate__banner--info {
  background-color: #f0f0f0;
  border: 1px solid #1d2019;
}

.cart-item-delivery-estimate__message {
  color: #555;
  font-size: 0.875rem;
}

.cart-item-delivery-estimate__label {
  color: #1d2019;
  display: inline;
  font-weight: 400;
  white-space: nowrap;
}

.cart-item-delivery-estimate__status {
  color: #1d2019;
  display: inline;
  font-weight: 400;
}

/* Use :deep() to target buttons injected via v-html */
.cart-item-delivery-estimate__status :deep(.cart-item-delivery-estimate__zipcode-button),
.cart-item-delivery-estimate__zipcode-button {
  background: none;
  border: none;
  color: #1d2019;
  cursor: pointer;
  font-size: inherit;
  font-weight: 700;
  padding: 0;
  text-decoration: underline;
  transition: opacity 0.2s;
}

.cart-item-delivery-estimate__status :deep(.cart-item-delivery-estimate__zipcode-button:hover),
.cart-item-delivery-estimate__zipcode-button:hover {
  opacity: 0.7;
}

.cart-item-delivery-estimate__edit-link {
  background: none;
  border: none;
  color: #1d2019;
  cursor: pointer;
  font-size: inherit;
  font-weight: 400;
  margin-left: auto;
  padding: 0;
  text-decoration: underline;
  transition: opacity 0.2s;
  white-space: nowrap;
}

.cart-item-delivery-estimate__edit-link:hover {
  opacity: 0.7;
}

.cart-item-delivery-estimate__modal {
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
}

.cart-item-delivery-estimate__modal-content {
  background-color: white;
  border-radius: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.16);
  max-width: 50%;
  position: relative;
  width: 90%;

  @media screen and (max-width: 768px) {
    max-width: none;
    width: 100vw;
  }
}

.cart-item-delivery-estimate__modal-header {
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  position: relative;
}

.cart-item-delivery-estimate__modal-header-icon {
  display: flex;
  align-items: center;
}

.cart-item-delivery-estimate__modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  color: #1d2019;
}

.cart-item-delivery-estimate__modal-close {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 1.75rem;
  padding: 0;
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.cart-item-delivery-estimate__modal-close:hover {
  color: #333;
}

.cart-item-delivery-estimate__modal-body {
  padding: 2rem;
}

.cart-item-delivery-estimate__modal-label {
  color: #1d2019;
  display: block;
  font-weight: 400;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
}

.cart-item-delivery-estimate__modal-input {
  display: flex;
  gap: 0;
  margin-bottom: 1.5rem;
}

.cart-item-delivery-estimate__modal-input input {
  border: 1px solid #ccc;
  border-radius: 0;
  color: #1d2019;
  flex: 1;
  font-size: 1rem;
  padding: 0.75rem 1rem;
}

.cart-item-delivery-estimate__modal-input input::placeholder {
  color: #999;
}

.cart-item-delivery-estimate__modal-input input:focus {
  border-color: #1d2019;
  box-shadow: none;
  outline: none;
}

.cart-item-delivery-estimate__modal-submit {
  align-items: center;
  background-color: #1d2019;
  border: 1px solid #1d2019;
  border-radius: 0;
  color: white;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 0.75rem 1rem;
}

.cart-item-delivery-estimate__modal-submit:hover {
  background-color: #333;
  border-color: #333;
}

.cart-item-delivery-estimate__modal-submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.cart-item-delivery-estimate__modal-error {
  color: #d32f2f;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
}

.cart-item-delivery-estimate__modal-info {
  border-top: 1px solid #e0e0e0;
  color: #555;
  font-size: 0.875rem;
  line-height: 1.5;
  padding-top: 1.5rem;
}

.cart-item-delivery-estimate__modal-info p {
  margin: 0 0 1rem 0;
}

.cart-item-delivery-estimate__modal-info p:last-child {
  margin-bottom: 0;
}
</style>
